export { CREATE_EVENT_CATEGORIES as UI_CATEGORIES } from "./createEventCategories";

export const physicalCategories = [
  { value: "concerts", label: "Concerts" },
  { value: "sports", label: "Sports" },
  { value: "theater", label: "Theater" },
  { value: "comedy", label: "Comedy" },
  { value: "festivals", label: "Festivals" },
  { value: "gaming", label: "Gaming" },
  { value: "conference", label: "Conference" },
  { value: "art", label: "Art & Culture" },
];

export const onlineCategories = [
  { value: "webinar", label: "Webinar" },
  { value: "workshop", label: "Workshop" },
  { value: "conference", label: "Conference" },
  { value: "networking", label: "Networking" },
  { value: "training", label: "Training" },
  { value: "other", label: "Other" },
];

export const physicalMusicTypes = [
  { value: "jazz", label: "Jazz" },
  { value: "rock", label: "Rock" },
  { value: "electronic", label: "Electronic" },
  { value: "hip-hop", label: "Hip Hop" },
  { value: "classical", label: "Classical" },
  { value: "pop", label: "Pop" },
];

export const onlineEventTypes = [
  { value: "live-stream", label: "Live Stream" },
  { value: "interactive", label: "Interactive" },
  { value: "recorded", label: "Recorded" },
  { value: "hybrid", label: "Hybrid" },
];

export function getCurrentCategories(eventFormat) {
  return eventFormat === "online" ? onlineCategories : physicalCategories;
}

export function getCurrentTypes(formData) {
  if (formData.eventFormat === "online") {
    return onlineEventTypes;
  }
  const category = formData.category;
  switch (category) {
    case "concerts":
    case "music":
      return physicalMusicTypes;
    case "sports":
      return [
        { value: "basketball", label: "Basketball" },
        { value: "football", label: "Football" },
        { value: "baseball", label: "Baseball" },
        { value: "soccer", label: "Soccer" },
        { value: "tennis", label: "Tennis" },
      ];
    case "theater":
    case "arts-theater":
      return [
        { value: "theater", label: "Theater" },
        { value: "musical", label: "Musical" },
        { value: "dance", label: "Dance" },
        { value: "opera", label: "Opera" },
        { value: "broadway", label: "Broadway" },
      ];
    case "comedy":
      return [
        { value: "stand-up", label: "Stand-up" },
        { value: "improv", label: "Improv" },
        { value: "sketch", label: "Sketch" },
        { value: "open-mic", label: "Open Mic" },
      ];
    case "festivals":
      return [
        { value: "music-festival", label: "Music Festival" },
        { value: "food-festival", label: "Food Festival" },
        { value: "cultural", label: "Cultural" },
        { value: "art-fair", label: "Art Fair" },
      ];
    case "gaming":
      return [
        { value: "esports", label: "Esports" },
        { value: "tournament", label: "Tournament" },
        { value: "lan-party", label: "LAN Party" },
        { value: "board-games", label: "Board Games" },
      ];
    case "conference":
      return [
        { value: "conference", label: "Conference" },
        { value: "summit", label: "Summit" },
        { value: "workshop", label: "Workshop" },
        { value: "networking", label: "Networking" },
      ];
    case "art":
    case "other":
      return [
        { value: "exhibition", label: "Exhibition" },
        { value: "gallery", label: "Gallery Opening" },
        { value: "cultural", label: "Cultural" },
        { value: "fundraiser", label: "Fundraiser" },
      ];
    default:
      return [
        { value: "general", label: "General" },
        { value: "premium", label: "Premium" },
        { value: "vip", label: "VIP" },
      ];
  }
}

export function getDefaultFormData(overrides = {}) {
  return {
    eventTitle: "",
    category: "",
    type: "",
    eventFormat: "physical",
    venueAddress: "",
    venueName: "",
    eventLink: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    eventImage: null,
    eventImagePreview: "",
    eventDescription: "",
    tags: [],
    tickets: [],
    eventVisibility: "public",
    isFeatured: false,
    ...overrides,
  };
}

export function createEmptyTicket() {
  return {
    id: Date.now() + Math.random(),
    ticketName: "",
    quantity: "",
    price: "0",
    serviceFee: "pass-to-buyer",
    salesStartOption: "immediately",
    salesStartDate: "",
    salesStartTime: "",
    salesEndOption: "event-start",
    salesEndDate: "",
    salesEndTime: "",
  };
}

export function canAdvanceStep(step, formData) {
  if (step === 0) {
    return (
      formData.eventTitle?.trim() &&
      formData.category &&
      formData.type &&
      (formData.eventDescription?.trim().length ?? 0) >= 10
    );
  }
  if (step === 1) {
    const hasDates =
      formData.startDate &&
      formData.startTime &&
      formData.endDate &&
      formData.endTime;
    if (!hasDates) return false;
    if (formData.eventFormat === "physical") {
      return !!(formData.venueAddress?.trim() || formData.venueName?.trim());
    }
    if (formData.eventFormat === "online") return !!formData.eventLink?.trim();
    return false;
  }
  if (step === 2) {
    const tickets = formData.tickets || [];
    return (
      tickets.length > 0 &&
      tickets.every((ticket) => {
        const qty = parseInt(String(ticket.quantity ?? ""), 10);
        const priceRaw = ticket.price;
        const hasPrice =
          priceRaw !== "" && priceRaw != null && !Number.isNaN(parseFloat(priceRaw));
        return (
          Boolean(ticket.ticketName?.trim()) &&
          Number.isFinite(qty) &&
          qty > 0 &&
          hasPrice
        );
      })
    );
  }
  if (step === 3) {
    return !!(formData.eventImage || formData.eventImagePreview);
  }
  return true;
}

export function getAdvanceBlockReason(step, formData) {
  if (step === 0) {
    if (!formData.eventTitle?.trim()) return "Enter an event name.";
    if (!formData.category) return "Select a category.";
    if (!formData.type) return "Select an event type.";
    if ((formData.eventDescription?.trim().length ?? 0) < 10) {
      return "Add a description (at least 10 characters).";
    }
    return "Complete all required basic info fields.";
  }
  if (step === 1) {
    if (!formData.startDate || !formData.startTime || !formData.endDate || !formData.endTime) {
      return "Fill in start and end date/time.";
    }
    if (formData.eventFormat === "physical" && !formData.venueAddress?.trim() && !formData.venueName?.trim()) {
      return "Enter a venue name or street address.";
    }
    if (formData.eventFormat === "online" && !formData.eventLink?.trim()) {
      return "Enter a streaming URL for online events.";
    }
    return "Complete date, time, and location fields.";
  }
  if (step === 2) {
    const tickets = formData.tickets || [];
    if (tickets.length === 0) return "Add at least one ticket tier.";
    for (const ticket of tickets) {
      if (!ticket.ticketName?.trim()) return "Enter a name for each ticket tier.";
      const qty = parseInt(String(ticket.quantity ?? ""), 10);
      if (!Number.isFinite(qty) || qty < 1) {
        return "Enter quantity available (at least 1) for each tier.";
      }
      const priceRaw = ticket.price;
      if (priceRaw === "" || priceRaw == null || Number.isNaN(parseFloat(priceRaw))) {
        return "Set a price for each tier (use Free for $0).";
      }
    }
    return "Complete ticket name, quantity, and price for each tier.";
  }
  if (step === 3) {
    return "Upload a cover image to continue.";
  }
  return "Complete the required fields to continue.";
}
